import i18n from 'scoreboard/components/i18n'
import _ from 'lodash'
import numericValidator from './numeric-validator'
import formatValue from 'scoreboard/components/format-value'
import moveDecimal from '../move-decimal'
import isPercentDisplayed from 'scoreboard/components/is-percent-displayed'

/**
 * Validates a number falls within a range, or is greater than a lower bound.
 *
 * @param {Number | function: Number} [low] inclusive (default); If not
 * provided, "Must be less than or equal to {}" messaging is used
 * @param {Number | function: Number} [high] inclusive (default); If not
 * provided, "Must be more than or equal to {}" messaging is used
 * @param {Object} formattingOptions
 * @param {String} [formattingOptions.dataType] the data type of the value
 * being validated (which is important for percentage metrics)
 * @param {Boolean} [opts.excludeLow=false] if true, "Must be more than {}"
 * messaging is used when comparing just against the highest bound
 * @param {Boolean} [opts.excludeHigh=false] if true, "Must be less than {}"
 * messaging is used when comparing just against the highest bound
 * @param {Boolean} [opts.excludeBetween=false] if true, "Must be between {} and {}"
 * messaging is used when comparing within a range
 * @param {Boolean} [opts.unformatted=false] if true, does not format the number
 * @return {numericRangeValidator}
 */
function numericRangeValidatorFactory(low, high, formattingOptions, opts = {}) {
	function numericRangeValidator(val) {
		if (opts.allowBlank && _.isEmpty(val)) return

		var err = numericValidator(val)
		if (err) {
			return err
		}

		if (isPercentDisplayed(_.get(formattingOptions, 'dataType'))) {
			val = moveDecimal(val, -2)
		}
		const lowVal = _.isFunction(low) ? low() : low
		const highVal = _.isFunction(high) ? high() : high

		var formattedLow = opts.unformatted ? lowVal : formatValue(_.extend({ value: lowVal }, formattingOptions))
		var formattedHigh = opts.unformatted ? highVal : formatValue(_.extend({ value: highVal }, formattingOptions))

		// if only given a lower bound, only check that (and give an appropriate error message)
		if (_.isNumber(lowVal) && !_.isNumber(highVal)) {
			if (opts.excludeLow && val <= lowVal) {
				return i18n.get('common.error.greaterThan.tooltip', formattedLow)
			} else if (val < lowVal) {
				return i18n.get('common.error.equalToOrGreaterThan.tooltip', formattedLow)
			}
		}
		if (_.isNumber(highVal) && !_.isNumber(lowVal)) {
			if (opts.excludeHigh && val >= highVal) {
				return i18n.get('common.error.lessThan.tooltip', formattedHigh)
			} else if (val > highVal) {
				return i18n.get('common.error.equalToOrLessThan.tooltip', formattedHigh)
			}
		}

		if (_.isNumber(lowVal) && _.isNumber(highVal)) {
			if (opts.excludeBetween && (val >= highVal || val <= lowVal)) {
				return i18n.get('common.error.between.tooltip', formattedLow, formattedHigh)
			} else if (val < lowVal || val > highVal) {
				return i18n.get('common.error.betweenInclusive.tooltip', formattedLow, formattedHigh)
			}
		}
	}
	return numericRangeValidator
}

export default numericRangeValidatorFactory

numericRangeValidatorFactory.__filename__ = 'components/validators/numeric-range-validator-factory.js'
